import type {
  CompetitionClassification,
  CompetitionRegistrationMode,
  CompetitionRegistrationStatus,
  CompetitionRegistrationView,
  CompetitionSeasonView,
  CreateCompetitionRegistrationRequest,
  EndCompetitionRegistrationRequest,
  TeamView,
  UpdateCompetitionRegistrationRequest,
} from '@stm/contracts';
import { competitionClassifications, isCalendarDate } from '@stm/contracts';
import type { TranslationKey } from '@stm/i18n';

export const registrationModeKeys: Readonly<Record<CompetitionRegistrationMode, TranslationKey>> = {
  native: 'registrations.mode.native',
  temporary_play_up: 'registrations.mode.temporary_play_up',
  exclusive_higher: 'registrations.mode.exclusive_higher',
};

export const registrationClassificationKeys: Readonly<
  Record<CompetitionClassification, TranslationKey>
> = {
  general: 'registrations.classification.general',
  girls: 'registrations.classification.girls',
  national_team_female: 'registrations.classification.national_team_female',
};

/** The classification named by a field value, or undefined when it is none of them. */
export function classificationOf(text: string): CompetitionClassification | undefined {
  return competitionClassifications.find((known) => known === text);
}

export const registrationStatusKeys: Readonly<
  Record<CompetitionRegistrationStatus, TranslationKey>
> = {
  active: 'registrations.status.active',
  ended: 'registrations.status.ended',
};

/** Teams a competition can register athletes through: same department and season, not archived. */
export function registrableTeams(
  teams: readonly TeamView[],
  competition: Pick<CompetitionSeasonView, 'departmentId' | 'seasonId'>,
): TeamView[] {
  return teams.filter(
    (team) =>
      team.departmentId === competition.departmentId &&
      team.seasonId === competition.seasonId &&
      team.status !== 'archived',
  );
}

export interface RegistrationForm {
  athleteId: string;
  teamId: string;
  mode: CompetitionRegistrationMode;
  validFrom: string;
  validTo: string;
  licenseReference: string;
  classification: CompetitionClassification;
}

export const emptyRegistrationForm: RegistrationForm = {
  athleteId: '',
  teamId: '',
  mode: 'native',
  validFrom: '',
  validTo: '',
  licenseReference: '',
  classification: 'general',
};

/** The create request, or null while the form is incomplete or its dates contradict each other. */
export function registrationRequest(
  form: RegistrationForm,
  competitionSeasonId: string,
): CreateCompetitionRegistrationRequest | null {
  if (form.athleteId === '' || form.teamId === '') return null;
  if (!isCalendarDate(form.validFrom)) return null;
  if (form.validTo !== '') {
    if (!isCalendarDate(form.validTo) || form.validTo < form.validFrom) return null;
  }
  const licenseReference = form.licenseReference.trim();
  return {
    competitionSeasonId,
    athleteId: form.athleteId,
    teamId: form.teamId,
    mode: form.mode,
    validFrom: form.validFrom,
    validTo: form.validTo === '' ? null : form.validTo,
    licenseReference: licenseReference === '' ? null : licenseReference,
    classification: form.classification,
  };
}

export interface RegistrationCorrectionForm {
  validTo: string;
  licenseReference: string;
  classification: CompetitionClassification;
}

export function correctionFormOf(
  registration: CompetitionRegistrationView,
): RegistrationCorrectionForm {
  return {
    validTo: registration.validTo ?? '',
    licenseReference: registration.licenseReference ?? '',
    classification: registration.classification,
  };
}

/**
 * Only the fields that differ from the registration are sent. Null when nothing changed or the end
 * day is invalid, so the save button stays off.
 */
export function correctionRequest(
  form: RegistrationCorrectionForm,
  registration: CompetitionRegistrationView,
): UpdateCompetitionRegistrationRequest | null {
  const request: UpdateCompetitionRegistrationRequest = {};
  const validTo = form.validTo === '' ? null : form.validTo;
  if (validTo !== null && (!isCalendarDate(validTo) || validTo < registration.validFrom)) {
    return null;
  }
  if (validTo !== registration.validTo) request.validTo = validTo;
  const licenseReference =
    form.licenseReference.trim() === '' ? null : form.licenseReference.trim();
  if (licenseReference !== registration.licenseReference) {
    request.licenseReference = licenseReference;
  }
  if (form.classification !== registration.classification) {
    request.classification = form.classification;
  }
  return Object.keys(request).length === 0 ? null : request;
}

export interface EndRegistrationForm {
  endedOn: string;
  reason: string;
}

/**
 * The end request, or null when the day is not a calendar day, is before the start, is after the
 * latest plausible day (the server holds the exact club-local today) or no reason was written.
 */
export function endRequest(
  form: EndRegistrationForm,
  registration: Pick<CompetitionRegistrationView, 'validFrom' | 'validTo'>,
  latestDay: string,
): EndCompetitionRegistrationRequest | null {
  const reason = form.reason.trim();
  if (reason === '' || !isCalendarDate(form.endedOn)) return null;
  if (form.endedOn < registration.validFrom || form.endedOn > latestDay) return null;
  if (registration.validTo !== null && form.endedOn > registration.validTo) return null;
  return { endedOn: form.endedOn, reason };
}

/** Active registrations first, then ended ones, each group in the order the server sent. */
export function activeFirst(
  registrations: readonly CompetitionRegistrationView[],
): CompetitionRegistrationView[] {
  return [
    ...registrations.filter((registration) => registration.status === 'active'),
    ...registrations.filter((registration) => registration.status !== 'active'),
  ];
}

export function registrationsPath(base: string, registrationId?: string, action?: 'end'): string {
  const root = `${base}/competition-registrations`;
  if (registrationId === undefined) return root;
  const one = `${root}/${encodeURIComponent(registrationId)}`;
  return action === undefined ? one : `${one}/${action}`;
}
